import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { label: "About", href: "#about" },
  { label: "Pricing", href: "/pricing" },
  { label: "Co-Creator", href: "/co-creator" },
  { label: "FAQ", href: "/faq" },
];

const CTA = { label: "Get Started", href: "#register" };

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileRounded, setMobileRounded] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      setMobileRounded(true);
    } else {
      const t = setTimeout(() => setMobileRounded(false), 300);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href: string) =>
    href.startsWith("#")
      ? location.pathname === "/"
      : location.pathname === href;

  const handleClick = (href: string) => {
    setMobileOpen(false);
    // Route-based link (e.g. /faq)
    if (!href.startsWith("#")) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    // Anchor link — scroll if on home, otherwise navigate home with state
    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: href } });
    }
  };

  return (
    <nav
      className={`fixed top-2 left-2 right-2 z-50 border transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ${mobileRounded ? "rounded-2xl" : "rounded-full"} ${
        scrolled || mobileRounded
          ? "bg-[#EFEBE5]/65 backdrop-blur-sm border border-[#E0D9CE]"
          : "bg-transparent border-transparent"
      }`}
    >
      {/* ── Desktop bar ── */}
      <div className="mx-auto px-4 flex items-center justify-between h-16">
        {/* Logo */}
        <div className="relative cursor-pointer" onClick={() => handleClick("#root")}>
          <img
            src="/assets/images/logo.svg"
            alt="Envizage"
            className="h-8 w-auto"
          />
        </div>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleClick(item.href)}
                className={`text-md font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.href)
                    ? "text-[#BF8C1A]"
                    : "text-[#4a4a4a] hover:text-[#1a1a2e]"
                }`}
              >
                {item.label}
              </button>
          ))}
          <button
            onClick={() => handleClick(CTA.href)}
            className="text-md font-semibold bg-[#222222] text-white px-5 py-2 rounded-full shadow-sm hover:bg-[#000000] transition-colors cursor-pointer whitespace-nowrap"
          >
            {CTA.label}
          </button>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-8 h-8 cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <i
            className={`text-2xl text-[#1a1a2e] ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
          ></i>
        </button>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        className="md:hidden overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: mobileOpen ? "400px" : "0px",
          opacity: mobileOpen ? 1 : 0,
        }}
      >
        <div className="bg-[#EFEBE5]/98 backdrop-blur-sm border-t border-[#E0D9CE] px-6 py-4 flex flex-col gap-1">
          {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleClick(item.href)}
                className={`text-md font-medium transition-colors cursor-pointer text-left py-2.5 border-b border-[#E0D9CE] last:border-0 whitespace-nowrap ${
                  isActive(item.href)
                    ? "text-[#BF8C1A]"
                    : "text-[#4a4a4a] hover:text-[#1a1a2e]"
                }`}
              >
                {item.label}
              </button>
          ))}
          <button
            onClick={() => handleClick(CTA.href)}
            className="text-md font-semibold bg-[#222222] text-white px-5 py-3 rounded-full hover:bg-[#000000] transition-colors cursor-pointer whitespace-nowrap mt-3"
          >
            {CTA.label}
          </button>
        </div>
      </div>
    </nav>
  );
}
